import { useLocation } from 'react-router-dom';
import useDashboardTourGate from './useDashboardTourGate';

/**
 * True while the dashboard tour owns the screen, so no other automatic popup
 * may open.
 *
 * Business rule: the tour goes first. Once the user acts on it — Skip, "Don't
 * show again" or Finish — everything else is free to appear.
 *
 * Why the tour's own `isEligible` is the whole answer: the dismissal key is
 * written by `markDismissed()` on exit, never on open (see
 * `useDashboardTourGate`). So `isEligible` is true from the moment the tour
 * qualifies, stays true for as long as it is on screen, and flips false the
 * instant the user dismisses it. No separate "is it open" flag is needed, and
 * consumers do not have to reach into the tour's local state.
 *
 * That last point is why this is a standalone hook rather than a value returned
 * from `useDashboardTour`: the consumers are scattered — one lives in
 * `WithSidebar` (every authenticated route), others in the dashboard page and in
 * a feature module — and none of them can see the tour component's state.
 *
 * Scoped to `/dashboard` because that is the only route the tour mounts on;
 * elsewhere there is nothing to protect and popups should behave normally.
 *
 * The wait is inherently bounded: the tour opens within `CATALOGUE_WAIT_MS` of
 * the dashboard mounting, and any exit clears the block.
 */
function useIsTourBlocking() {
  const { isEligible } = useDashboardTourGate();
  const { pathname } = useLocation();

  return isEligible && pathname === '/dashboard';
}

export default useIsTourBlocking;
